import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {useAppDispatch} from "../../../../../../../redux";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import {InfoOutlined} from "@mui/icons-material";
import Api from "../../../../../../../../core/services/api/api";
import {CustomerMemberShipNames} from "../../../../../../../../core/constants/enums";
import {Alert} from "@mui/lab";

export const ClientPlatformConfigurationsSection = () => {
    const [loadingRetailCustomer, setLoadingRetailUser] = useState(false);
    const [configs, setConfigs] = useState({});
    const [submitting, setSubmitting] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();

    const loading = useMemo(() => loadingRetailCustomer, [loadingRetailCustomer]);

    /**
     * Fetches the configurations data from the server
     * @type {(function(): Promise<void>)}
     */
    const getPartnerRetailUser = useCallback(async () => {
        setLoadingRetailUser(true);
        const response = await Api.getPartnerRetailUser();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setConfigs(prevState => ({
                ...prevState,
                retailUser: response.data?.userId ? response.data : undefined,
            }));
        }
        setLoadingRetailUser(false);
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - Fetches the configurations data from the server
     */
    useEffect(() => {
        void getPartnerRetailUser();
    }, [getPartnerRetailUser]);

    /**
     * Updates the sale promotional text of the partner information.
     * @type {(function(*): Promise<void>)}
     */
    const updatePartnerRetailUser = useCallback(async (value) => {
        if (value === configs.retailUser) {
            return {resultFlag: true};
        }
        const isRemoval = !value;
        const isAdd = value && !configs?.retailUser;
        const isUpdate = value && !!configs?.retailUser;
        const confirmed = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                ...(isRemoval && {
                    title: 'Confirm Removal of Retail User',
                    description: 'By disabling guest user access, guest users will not be able to navigate through your website without logging in. Do you want to proceed?'
                }),
                ...(isAdd && {
                    title: 'Confirm Addition of Retail User',
                    description: 'By enabling guest user access, guest users will be able to navigate through your website using the retail user account with some limitations, and see retail pricing. This essentially opens your website to be more of B2C than a B2B experience. Do you want to proceed?'
                }),
                ...(isUpdate && {
                    title: 'Confirm Update of Retail User Access',
                    description: `are you sure you would like to update the guest user to be ${value?.firstname ?? ''} ${value?.lastname ?? ''} (${value?.spireCustomerTitle ?? ''})?`
                }),
                cancelButtonText: "Cancel",
                confirmButtonText: "Proceed",
                maxWidth: 'sm',
                fullWidth: false,
                onClose: (confirmed) => {
                    resolve(confirmed);
                    return Promise.resolve(true);
                }
            }));
        })
        if (!confirmed) {
            return;
        }
        setSubmitting(prevState => [...prevState, 'retailUser']);
        const response = await Api.updatePartnerRetailUser({userId: value?.userId ?? null,})
        if (!isMounted())
            return;
        setSubmitting(prevState => prevState.filter(e => e !== 'retailUser'));
        if (response?.resultFlag) {
            setConfigs(prevState => ({
                ...prevState,
                retailUser: value,
            }));
        }
        return response;
    }, [configs.retailUser, dispatch, isMounted]);

    /**
     * Fetches Territory options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching Territory options
     */
    const getActiveMemberOptions = useCallback(async (cancelToken, keywords) => {
        // @ts-ignore - API type definition is not complete
        const response = await Api.searchCurrentUsers({
            paginationInfo: {pageSize: 20, currentPage: 1},
            filters: {
                keyword: keywords,
                memberShipNames: [CustomerMemberShipNames.active],
                isOrderBySubmitDateADescending: false,
            },
        }, cancelToken)
        if (!isMounted())
            return [];
        return response?.data?.resultList?.map(e => ({
            ...e,
            userId: e.id,
            firstname: e.firstName,
            lastname: e.lastName,
            spireCustomerTitle: e.customer?.name ?? e.company ?? "",
        })) ?? [];
    }, [isMounted]);

    /**
     * Determines if two Territory options are equal based on their IDs
     */
    const isMemberOptionEqualToValue = useCallback((option, value) => option.userId === value?.userId, []);

    /**
     * Extracts the display label from a Territory option
     */
    const getMemberOptionLabel = useCallback((option) => [option.firstname, option.lastname].filter(Boolean).join(' ').concat(option.spireCustomerTitle ? ` (${option.spireCustomerTitle ?? ""})` : ""), []);

    /**
     * Gets the unique key for a Territory option
     */
    const getMemberOptionKey = useCallback((option) => option.userId, []);

    return (
        <>
            {
                loading &&
                Array.from({length: 1}).map((_, index) => (
                    <Grid2 xs={12} key={index}>
                        <div className={'loading access-level w-100'}>
                            <div>
                                <div/>
                            </div>
                        </div>
                    </Grid2>
                ))
            }
            {
                !loading &&
                <>
                    <Grid2 xs={12}>
                        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Client Platform Configurations
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>
                                <Grid2 xs={12} paddingInline={3}>
                                    <ConfigurableInfoBlock
                                        title={"Retail User (Guest User Access)"}
                                        inputType={ConfigurableInfoBlockInputTypes.autocomplete}
                                        onValueChanged={(value) => updatePartnerRetailUser(value)}
                                        loading={submitting.includes('retailUser')}
                                        inputProps={{
                                            label: 'Retail User',
                                            value: configs.retailUser,
                                            keywordsBased: true,
                                            canSearchEmptyKeywords: true,
                                            isOptionEqualToValue: isMemberOptionEqualToValue,
                                            getOptionLabel: getMemberOptionLabel,
                                            getOptionKey: getMemberOptionKey,
                                            getOptions: getActiveMemberOptions,
                                            options: [],
                                            fullWidth: true,
                                        }}
                                        showCustomIcons
                                        customIcons={[
                                            <Tooltip
                                                key={1}
                                                title={
                                                    'By designating a retail user, your client website will be accessible to guest users (who by default will be able to navigate through your' +
                                                    ' website  with some limitations, and see retail pricing. This essentially opens your website to be more of B2C than a B2B experience. If you' +
                                                    ' do not designate a retail user, your client website will not be accessible to guest users and they will be required to login with a valid' +
                                                    '  user account to access the client platform. Please consider carefully before making changes to this setting.'
                                                }
                                            >
                                                <InfoOutlined color={'primary'}/>
                                            </Tooltip>
                                        ]}
                                    >
                                        <Alert severity={'info'} sx={{marginBlock: 2}}>
                                            By designating a retail user, your client website will be accessible to guest users - who by default will be able to navigate through your website using
                                            that retail user account with some limitations, and see retail pricing.
                                            This essentially opens your website to be more of B2C than a B2B experience.
                                        </Alert>
                                    </ConfigurableInfoBlock>
                                </Grid2>
                            </Grid2>
                        </Card>
                    </Grid2>
                </>
            }
        </>
    )
}